import type { Plan } from '@fabric-app/business/pricing/pricing.types';
import type { PurchasesOffering } from '@revenuecat/purchases-capacitor';
import { PACKAGE_TYPE, PRODUCT_CATEGORY, PRODUCT_TYPE } from '@revenuecat/purchases-capacitor';
import { formatCurrency } from '@fabric-app/utils/number';

const calculatePricing = (yearlyPrice: number) => {
  const monthlyPrice = yearlyPrice / 12;
  const weeklyPrice = yearlyPrice / 52;

  return {
    pricePerMonth: monthlyPrice,
    pricePerWeek: weeklyPrice,
    pricePerYear: yearlyPrice,
    pricePerMonthString: formatCurrency(monthlyPrice, 'USD'),
    pricePerWeekString: formatCurrency(weeklyPrice, 'USD'),
    pricePerYearString: formatCurrency(yearlyPrice, 'USD'),
  };
};

/**
 * Creates a fake RevenueCat offering from a plan object.
 * This allows using the revenue cat query without adding extra conditions to the components.
 * @param offeringId
 * @param plan
 * @returns the fake offering
 */
const createFakeRevenueCatOfferingFromPlan = (
  offeringId: string,
  plan: Plan,
): PurchasesOffering => {
  const yearlyProductPricing = calculatePricing(plan.prices.billedYearly);
  const monthlyProductPricing = calculatePricing(plan.prices.billedMonthly * 12);

  const stripePlanIdYearly = plan.prices.stripePlanIdYearly ?? '';
  const stripePlanIdMonthly = plan.prices.stripePlanIdMonthly ?? '';

  const annualIdentifier = `${offeringId}_yearly`;
  const monthlyIdentifier = `${offeringId}_monthly`;

  const res = {
    _meta: 'f',
    identifier: offeringId,
    serverDescription: plan.title,
    metadata: {},
    availablePackages: [],
    annual: {
      identifier: annualIdentifier,
      packageType: PACKAGE_TYPE.ANNUAL,
      presentedOfferingContext: {
        offeringIdentifier: offeringId,
        placementIdentifier: null,
        targetingContext: null,
      },
      offeringIdentifier: offeringId,
      product: {
        identifier: stripePlanIdYearly,
        title: plan.title,
        description: plan.title,
        currencyCode: 'USD',
        defaultOption: null,
        discounts: [],
        price: yearlyProductPricing.pricePerYear,
        priceString: yearlyProductPricing.pricePerYearString,
        pricePerMonth: yearlyProductPricing.pricePerMonth,
        pricePerMonthString: yearlyProductPricing.pricePerMonthString,
        pricePerWeek: yearlyProductPricing.pricePerWeek,
        pricePerWeekString: yearlyProductPricing.pricePerWeekString,
        pricePerYear: yearlyProductPricing.pricePerYear,
        pricePerYearString: yearlyProductPricing.pricePerYearString,
        introPrice: null,
        presentedOfferingIdentifier: annualIdentifier,
        presentedOfferingContext: {
          offeringIdentifier: offeringId,
          placementIdentifier: null,
          targetingContext: null,
        },
        productCategory: PRODUCT_CATEGORY.SUBSCRIPTION,
        productType: PRODUCT_TYPE.AUTO_RENEWABLE_SUBSCRIPTION,
        subscriptionOptions: [],
        subscriptionPeriod: 'P1Y',
      },
    },
    monthly: {
      identifier: monthlyIdentifier,
      packageType: PACKAGE_TYPE.MONTHLY,
      presentedOfferingContext: {
        offeringIdentifier: offeringId,
        placementIdentifier: null,
        targetingContext: null,
      },
      offeringIdentifier: offeringId,
      product: {
        identifier: stripePlanIdMonthly,
        title: plan.title,
        description: plan.title,
        currencyCode: 'USD',
        defaultOption: null,
        discounts: [],
        price: monthlyProductPricing.pricePerMonth,
        priceString: monthlyProductPricing.pricePerMonthString,
        pricePerMonth: monthlyProductPricing.pricePerMonth,
        pricePerMonthString: monthlyProductPricing.pricePerMonthString,
        pricePerWeek: monthlyProductPricing.pricePerWeek,
        pricePerWeekString: monthlyProductPricing.pricePerWeekString,
        pricePerYear: monthlyProductPricing.pricePerYear,
        pricePerYearString: monthlyProductPricing.pricePerYearString,
        introPrice: null,
        presentedOfferingIdentifier: monthlyIdentifier,
        presentedOfferingContext: {
          offeringIdentifier: offeringId,
          placementIdentifier: null,
          targetingContext: null,
        },
        productCategory: PRODUCT_CATEGORY.SUBSCRIPTION,
        productType: PRODUCT_TYPE.AUTO_RENEWABLE_SUBSCRIPTION,
        subscriptionOptions: [],
        subscriptionPeriod: 'P1M',
      },
    },
    lifetime: null,
    sixMonth: null,
    threeMonth: null,
    twoMonth: null,
    weekly: null,
  };
  return res;
};

export default createFakeRevenueCatOfferingFromPlan;
